import React, { useState, useEffect, useCallback } from "react";
import { notify } from "../../components/ui/notify";
import {
  Typography,
  Modal,
  Input,
  Button,
  Table,
  Select,
  Tag,
} from "antd";
import {
  SettingOutlined,
  ScheduleOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  AlertOutlined,
  PlusOutlined,
  DeleteOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  ApartmentOutlined,
  TagOutlined,
  TeamOutlined,
  AuditOutlined,
  ReconciliationOutlined,
  MailOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import {
  actionCorreosUsersGet,
  actionCorreosUsersCreate,
  actionCorreosUsersUpdate,
  actionCorreosUsersDelete,
  actionCorreosUsersSetAutoridades,
} from "../../redux/actions/correos_users/correos_users";
import { actionAutoridadesAllGet } from "../../redux/actions/autoridades/autoridades";
import {
  actionCatalogoConfigGet,
  actionCatalogoConfigCreate,
  actionCatalogoConfigDelete,
} from "../../redux/actions/catalogos_config/catalogos_config";
import {
  actionObjetosGet,
  actionObjetoCreate,
  actionObjetoDeleteByCode,
} from "../../redux/actions/objetos/objetos";
import {
  actionMedioNotificacionGet,
  actionMedioNotificacionCreate,
  actionMedioNotificacionDeleteByCode,
} from "../../redux/actions/medio_notificacion/medio_notificacion";
import {
  actionCiudadesGet,
  actionCiudadCreate,
  actionCiudadDeleteByCode,
} from "../../redux/actions/ciudades/ciudades";
import { actionEstadosGet } from "../../redux/actions/estados/estados";
import {
  actionIdentificacionCiudadGet,
  actionIdentificacionCiudadCreate,
  actionIdentificacionCiudadDeleteByCode,
} from "../../redux/actions/identificacion_ciudades/identificacion_ciudades";
import {
  actionTipo_autoridadesGet,
  actionTipo_autoridadCreate,
  actionTipo_autoridadDeleteByCode,
} from "../../redux/actions/tipo_autoridades/tipo_autoridades";
import { actionMateriasGet } from "../../redux/actions/materias/materias";
import {
  FORMA_NOTIFICACION_LABELS,
  ORIGEN_NOTIFICADO_LABELS,
} from "../../utils/notificacionCatalogos";
import { medioElectronicoLabel } from "../../utils/tribunalCatalogos";
import "./ConfiguracionPage.css";

const { Title, Text } = Typography;

function listFromState(stateSlice) {
  if (Array.isArray(stateSlice)) return [];
  return stateSlice?.items || [];
}

// ── Catálogo simple conectado (chips) ───────────────────────────────────────
// Usado para los catálogos que solo necesitan nombre + active. Puede leer de
// un selector de Redux (cuando el action ya guarda en el store) o manejar su
// propio estado local (cuando el action solo regresa la respuesta).
function CatalogoChips({
  icon,
  title,
  desc,
  placeholder,
  emptyText,
  loadAction,
  addAction,
  deleteAction,
  selector,
  getLabel = (i) => i.nombre,
  getKey = (i) => i.id,
  getDeleteKey = (i) => i.id,
  maxLength = 150,
}) {
  const dispatch = useDispatch();
  const storeData = useSelector(selector || (() => null));
  const [localItems, setLocalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await dispatch(loadAction());
      if (!selector) setLocalItems(data?.items || (Array.isArray(data) ? data : []));
    } catch {
      notify.error("No se pudo cargar la información.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const items = selector ? listFromState(storeData) : localItems;

  const handleAdd = async () => {
    const nombre = input.trim();
    if (!nombre) return;
    setAdding(true);
    try {
      await dispatch(addAction(nombre));
      setInput("");
      await refresh();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo agregar.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: `¿Eliminar "${getLabel(item)}"?`,
      content: "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(deleteAction(getDeleteKey(item)));
          await refresh();
        } catch {
          notify.error("No se pudo eliminar.");
        }
      },
    });
  };

  const iconWithClass = React.cloneElement(icon, { className: "cfg-block-icon" });

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        {iconWithClass}
        <div>
          <div className="cfg-block-title">{title}</div>
          <div className="cfg-block-desc">{desc}</div>
        </div>
      </div>

      <div className="cfg-tipo-add-row">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="cfg-tipo-input"
          onPressEnter={handleAdd}
          maxLength={maxLength}
          disabled={adding}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!input.trim()}
          loading={adding}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>

      {loading ? (
        <div className="cfg-tipo-empty">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="cfg-tipo-empty">{emptyText}</div>
      ) : (
        <div className="cfg-tipo-chips">
          {items.map((item) => (
            <div key={getKey(item)} className="cfg-tipo-chip">
              <span className="cfg-tipo-chip-label">{getLabel(item)}</span>
              <button
                type="button"
                className="cfg-tipo-chip-del"
                onClick={() => handleDelete(item)}
                title="Eliminar"
              >
                <DeleteOutlined />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Avisos de inicio (lista de texto largo, no chips) ───────────────────────
function AvisosBlock() {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await dispatch(actionCatalogoConfigGet("avisos"));
      setItems(data?.items || []);
    } catch {
      notify.error("No se pudieron cargar los avisos.");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = async () => {
    const texto = input.trim();
    if (!texto) return;
    setAdding(true);
    try {
      await dispatch(actionCatalogoConfigCreate("avisos", texto));
      setInput("");
      await refresh();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo agregar el aviso.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = (aviso) => {
    Modal.confirm({
      title: "¿Eliminar aviso?",
      content: <span>Se eliminará el aviso. Esta acción no se puede deshacer.</span>,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(actionCatalogoConfigDelete("avisos", aviso.id));
          await refresh();
        } catch {
          notify.error("No se pudo eliminar el aviso.");
        }
      },
    });
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <AlertOutlined className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Avisos de inicio</div>
          <div className="cfg-block-desc">
            Mensajes que se muestran en la pantalla de inicio para todos los usuarios
          </div>
        </div>
      </div>
      <div className="cfg-tipo-add-row">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe el aviso..."
          className="cfg-tipo-input"
          onPressEnter={handleAdd}
          maxLength={300}
          disabled={adding}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!input.trim()}
          loading={adding}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>
      {loading ? (
        <div className="cfg-tipo-empty">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="cfg-tipo-empty">Sin avisos configurados</div>
      ) : (
        <div className="cfg-aviso-list">
          {items.map((a) => (
            <div key={a.id} className="cfg-aviso-item">
              <span className="cfg-aviso-item-text">{a.nombre}</span>
              <button
                type="button"
                className="cfg-tipo-chip-del"
                onClick={() => handleDelete(a)}
                title="Eliminar"
              >
                <DeleteOutlined />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ciudades (nombre + estado) ──────────────────────────────────────────────
function CiudadesBlock() {
  const dispatch = useDispatch();
  const [inputNombre, setInputNombre] = useState("");
  const [inputEstado, setInputEstado] = useState(undefined);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    dispatch(actionEstadosGet());
    dispatch(actionCiudadesGet());
  }, [dispatch]);

  const estadosItems = listFromState(useSelector((state) => state.estados?.data));
  const estadoOptions = estadosItems.map((e) => ({ label: e.nombre, value: e.id }));
  const estadoNombreById = Object.fromEntries(estadosItems.map((e) => [e.id, e.nombre]));

  const ciudades = listFromState(useSelector((state) => state.ciudades?.data));

  const canAdd = inputNombre.trim() && inputEstado != null;

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    try {
      await dispatch(actionCiudadCreate({ nombre: inputNombre.trim(), id_estado: inputEstado }));
      setInputNombre("");
      setInputEstado(undefined);
      notify.success("Ciudad agregada.");
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo agregar la ciudad.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = (ciudad) => {
    Modal.confirm({
      title: "¿Eliminar ciudad?",
      content: (
        <span>
          Se eliminará <strong>{ciudad.nombre}</strong>. Esta acción no se puede deshacer.
        </span>
      ),
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(actionCiudadDeleteByCode(ciudad.nombre));
          notify.success("Ciudad eliminada.");
        } catch {
          notify.error("No se pudo eliminar la ciudad.");
        }
      },
    });
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <EnvironmentOutlined className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Ciudades</div>
          <div className="cfg-block-desc">Ciudades disponibles en el sistema, asociadas a un estado</div>
        </div>
      </div>

      <div className="cfg-ciudad-form">
        <Input
          value={inputNombre}
          onChange={(e) => setInputNombre(e.target.value)}
          placeholder="Nombre de la ciudad..."
          className="cfg-tipo-input"
          maxLength={80}
          disabled={adding}
        />
        <Select
          value={inputEstado}
          onChange={setInputEstado}
          options={estadoOptions}
          placeholder="Estado..."
          style={{ minWidth: 180 }}
          disabled={adding}
          showSearch
          optionFilterProp="label"
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!canAdd}
          loading={adding}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>

      {ciudades.length === 0 ? (
        <div className="cfg-tipo-empty">Sin ciudades registradas</div>
      ) : (
        <div className="cfg-tipo-chips">
          {ciudades.map((c) => (
            <div key={c.id} className="cfg-tipo-chip">
              <span className="cfg-tipo-chip-label">
                {c.nombre}
                <span className="cfg-ciudad-clave-badge">{estadoNombreById[c.id_estado] || ""}</span>
              </span>
              <button
                type="button"
                className="cfg-tipo-chip-del"
                onClick={() => handleDelete(c)}
                title="Eliminar"
              >
                <DeleteOutlined />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Identificación por ciudad (ciudad + competencia + clave) ───────────────
function IdentificacionCiudadBlock() {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [inputNombre, setInputNombre] = useState("");
  const [inputCiudad, setInputCiudad] = useState(undefined);
  const [inputCompetencia, setInputCompetencia] = useState(undefined);

  useEffect(() => {
    dispatch(actionCiudadesGet());
  }, [dispatch]);

  const ciudadesItems = listFromState(useSelector((state) => state.ciudades?.data));
  const ciudadOptions = ciudadesItems.map((c) => ({ label: c.nombre, value: c.id }));
  const ciudadNombreById = Object.fromEntries(ciudadesItems.map((c) => [c.id, c.nombre]));
  const competenciaOptions = competencias.map((c) => ({ label: c.nombre, value: c.id }));
  const competenciaNombreById = Object.fromEntries(competencias.map((c) => [c.id, c.nombre]));

  const refresh = useCallback(async () => {
    try {
      const [identData, compData] = await Promise.all([
        dispatch(actionIdentificacionCiudadGet()),
        dispatch(actionCatalogoConfigGet("tipos_competencia")),
      ]);
      setItems(identData?.items || []);
      setCompetencias(compData?.items || []);
    } catch {
      notify.error("No se pudo cargar la información.");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canAdd = inputNombre.trim() && inputCiudad != null && inputCompetencia != null;

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    try {
      await dispatch(
        actionIdentificacionCiudadCreate({
          nombre: inputNombre.trim(),
          id_ciudad: inputCiudad,
          id_competencia: inputCompetencia,
        })
      );
      setInputNombre("");
      setInputCiudad(undefined);
      setInputCompetencia(undefined);
      await refresh();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo agregar.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: `¿Eliminar "${item.nombre}"?`,
      content: "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(actionIdentificacionCiudadDeleteByCode(item.nombre));
          await refresh();
        } catch {
          notify.error("No se pudo eliminar.");
        }
      },
    });
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <EnvironmentOutlined className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Identificación por ciudad</div>
          <div className="cfg-block-desc">
            Clave de identificación para expedientes, según la ciudad y la competencia (ej. Mexicali + Local → MXLI/CI)
          </div>
        </div>
      </div>

      <div className="cfg-ciudad-form">
        <Select
          value={inputCiudad}
          onChange={setInputCiudad}
          options={ciudadOptions}
          placeholder="Ciudad..."
          style={{ minWidth: 160 }}
          disabled={adding}
          showSearch
          optionFilterProp="label"
        />
        <Select
          value={inputCompetencia}
          onChange={setInputCompetencia}
          options={competenciaOptions}
          placeholder="Competencia..."
          style={{ minWidth: 160 }}
          disabled={adding}
          showSearch
          optionFilterProp="label"
        />
        <Input
          value={inputNombre}
          onChange={(e) => setInputNombre(e.target.value)}
          placeholder="Clave (ej. MXLI/CI)..."
          className="cfg-ciudad-clave-input"
          maxLength={150}
          disabled={adding}
          onPressEnter={() => canAdd && handleAdd()}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!canAdd}
          loading={adding}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>

      {loading ? (
        <div className="cfg-tipo-empty">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="cfg-tipo-empty">Sin identificaciones registradas</div>
      ) : (
        <div className="cfg-tipo-chips">
          {items.map((it) => (
            <div key={it.id} className="cfg-tipo-chip">
              <span className="cfg-tipo-chip-label">
                {ciudadNombreById[it.id_ciudad] || "—"} · {competenciaNombreById[it.id_competencia] || "—"}
                <span className="cfg-ciudad-clave-badge">{it.nombre}</span>
              </span>
              <button
                type="button"
                className="cfg-tipo-chip-del"
                onClick={() => handleDelete(it)}
                title="Eliminar"
              >
                <DeleteOutlined />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tipos de autoridad (nombre + materia + clave) ───────────────────────────
function TiposAutoridadBlock() {
  const dispatch = useDispatch();
  const [inputNombre, setInputNombre] = useState("");
  const [inputKey, setInputKey] = useState("");
  const [inputMateria, setInputMateria] = useState(undefined);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    dispatch(actionMateriasGet());
    dispatch(actionTipo_autoridadesGet());
  }, [dispatch]);

  const materiasItems = listFromState(useSelector((state) => state.materias?.data));
  const materiaOptions = materiasItems.map((m) => ({ label: m.nombre, value: m.id }));
  const materiaNombreById = Object.fromEntries(materiasItems.map((m) => [m.id, m.nombre]));

  const tipos = listFromState(useSelector((state) => state.tipo_autoridad?.data));

  const canAdd = inputNombre.trim() && inputKey.trim() && inputMateria != null;

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    try {
      await dispatch(
        actionTipo_autoridadCreate({
          nombre: inputNombre.trim(),
          autoridad_key: inputKey.trim(),
          id_materia: inputMateria,
        })
      );
      setInputNombre("");
      setInputKey("");
      setInputMateria(undefined);
      notify.success("Tipo de autoridad agregado.");
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo agregar.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: `¿Eliminar "${item.nombre}"?`,
      content: "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(actionTipo_autoridadDeleteByCode(item.autoridad_key));
          notify.success("Eliminado.");
        } catch {
          notify.error("No se pudo eliminar.");
        }
      },
    });
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <SafetyCertificateOutlined className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Tipos de autoridad</div>
          <div className="cfg-block-desc">
            Tipos de autoridad disponibles en los expedientes (ej. Conciliación, Tribunal, Junta Conciliación)
          </div>
        </div>
      </div>

      <div className="cfg-ciudad-form">
        <Input
          value={inputNombre}
          onChange={(e) => setInputNombre(e.target.value)}
          placeholder="Nombre..."
          className="cfg-tipo-input"
          maxLength={255}
          disabled={adding}
        />
        <Select
          value={inputMateria}
          onChange={setInputMateria}
          options={materiaOptions}
          placeholder="Materia..."
          style={{ minWidth: 160 }}
          disabled={adding}
          showSearch
          optionFilterProp="label"
        />
        <Input
          value={inputKey}
          onChange={(e) => setInputKey(e.target.value)}
          placeholder="Clave (autoridad_key)..."
          className="cfg-ciudad-clave-input"
          maxLength={255}
          disabled={adding}
          onPressEnter={() => canAdd && handleAdd()}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!canAdd}
          loading={adding}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>

      {tipos.length === 0 ? (
        <div className="cfg-tipo-empty">Sin tipos de autoridad registrados</div>
      ) : (
        <div className="cfg-tipo-chips">
          {tipos.map((t) => (
            <div key={t.id} className="cfg-tipo-chip">
              <span className="cfg-tipo-chip-label">
                {t.nombre}
                <span className="cfg-ciudad-clave-badge">{materiaNombreById[t.id_materia] || t.autoridad_key}</span>
              </span>
              <button
                type="button"
                className="cfg-tipo-chip-del"
                onClick={() => handleDelete(t)}
                title="Eliminar"
              >
                <DeleteOutlined />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Panel General ──────────────────────────────────────────────────────────
function PanelGeneral() {
  return (
    <div className="cfg-panel-body">
      <AvisosBlock />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CiudadesBlock />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <IdentificacionCiudadBlock />
    </div>
  );
}

// ── Panel Agenda ───────────────────────────────────────────────────────────
function PanelAgenda() {
  return (
    <div className="cfg-panel-body">
      <CatalogoChips
        icon={<TagOutlined />}
        title="Tipos de agenda"
        desc="Categorías que se asignan al crear un evento de agenda"
        placeholder="Nombre del nuevo tipo..."
        emptyText="Sin tipos registrados"
        loadAction={() => actionCatalogoConfigGet("tipos_agenda")}
        addAction={(nombre) => actionCatalogoConfigCreate("tipos_agenda", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("tipos_agenda", id)}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CorreosSection />
    </div>
  );
}

// ── Panel Competencias (incluye Tipos de Autoridad) ────────────────────────
function PanelCompetencias() {
  return (
    <div className="cfg-panel-body">
      <CatalogoChips
        icon={<ApartmentOutlined />}
        title="Tipos de competencia"
        desc="Tipos de competencia disponibles en los expedientes (ej. Local, Federal)"
        placeholder="Nombre del tipo de competencia..."
        emptyText="Sin tipos de competencia registrados"
        loadAction={() => actionCatalogoConfigGet("tipos_competencia")}
        addAction={(nombre) => actionCatalogoConfigCreate("tipos_competencia", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("tipos_competencia", id)}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <TiposAutoridadBlock />
    </div>
  );
}

// (Panel Despachos eliminado: los despachos se administran desde Usuarios > Permisos)

// ── Panel Centro de Conciliación ───────────────────────────────────────────
function PanelCentroConciliacion() {
  return (
    <div className="cfg-panel-body">
      <CatalogoChips
        icon={<TeamOutlined />}
        title="Medios de notificación"
        desc="Canales para notificar a las partes en el proceso de conciliación (ej. WhatsApp, Correo Electrónico, Físicamente)"
        placeholder="Nombre del medio de notificación..."
        emptyText="Sin medios de notificación registrados"
        maxLength={150}
        loadAction={() => actionMedioNotificacionGet()}
        addAction={(nombre) => actionMedioNotificacionCreate({ nombre })}
        deleteAction={(nombre) => actionMedioNotificacionDeleteByCode(nombre)}
        getDeleteKey={(i) => i.nombre}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<ReconciliationOutlined />}
        title="Forma de notificación"
        desc="Quién notifica al trabajador al crear un expediente prejudicial (ej. Solicitante, Notificador CCL)"
        placeholder="Nombre de la forma de notificación..."
        emptyText="Sin formas de notificación registradas"
        maxLength={60}
        loadAction={() => actionCatalogoConfigGet("forma_notificacion")}
        addAction={(nombre) => actionCatalogoConfigCreate("forma_notificacion", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("forma_notificacion", id)}
        getLabel={(i) => FORMA_NOTIFICACION_LABELS[i.nombre] || i.nombre}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<BankOutlined />}
        title="A quién se le notificó"
        desc="Origen de a quién se notificó cuando la forma de notificación es por Notificador CCL (ej. Empresa, Despacho)"
        placeholder="Nombre del origen..."
        emptyText="Sin opciones registradas"
        maxLength={60}
        loadAction={() => actionCatalogoConfigGet("origen_notificado")}
        addAction={(nombre) => actionCatalogoConfigCreate("origen_notificado", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("origen_notificado", id)}
        getLabel={(i) => ORIGEN_NOTIFICADO_LABELS[i.nombre] || i.nombre}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<AppstoreOutlined />}
        title="Objetos de la solicitud"
        desc="Objetos del litigio en conciliación (ej. Despido, Pago de prestaciones, Rescisión, Reinstalación)"
        placeholder="Nombre del objeto de la solicitud..."
        emptyText="Sin objetos de la solicitud registrados"
        maxLength={55}
        loadAction={() => actionObjetosGet()}
        addAction={(nombre) => actionObjetoCreate({ nombre })}
        deleteAction={(nombre) => actionObjetoDeleteByCode(nombre)}
        selector={(state) => state.objetos?.data}
        getDeleteKey={(i) => i.nombre}
      />
    </div>
  );
}

// ── Panel Tribunal ─────────────────────────────────────────────────────────
function PanelTribunal() {
  return (
    <div className="cfg-panel-body">
      <CatalogoChips
        icon={<AppstoreOutlined />}
        title="Medios electrónicos"
        desc="Medios electrónicos para notificaciones en tribunal (ej. WhatsApp, Correo, SMS, Redes sociales, Videos)"
        placeholder="Nombre del medio electrónico..."
        emptyText="Sin medios electrónicos registrados"
        loadAction={() => actionCatalogoConfigGet("medios_electronicos")}
        addAction={(nombre) => actionCatalogoConfigCreate("medios_electronicos", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("medios_electronicos", id)}
        getLabel={(i) => medioElectronicoLabel(i.nombre)}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<AuditOutlined />}
        title="Acciones intentadas"
        desc="Acciones intentadas disponibles en los expedientes de tribunal (ej. Despido, Rescisión, Reinstalación)"
        placeholder="Nombre de la acción intentada..."
        emptyText="Sin acciones intentadas registradas"
        loadAction={() => actionCatalogoConfigGet("acciones_intentadas")}
        addAction={(nombre) => actionCatalogoConfigCreate("acciones_intentadas", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("acciones_intentadas", id)}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<TagOutlined />}
        title="Prestaciones reclamadas"
        desc="Prestaciones reclamadas en los expedientes (ej. Prima de Antigüedad, Vacaciones, Aguinaldo, Horas Extras)"
        placeholder="Nombre de la prestación reclamada..."
        emptyText="Sin prestaciones reclamadas registradas"
        loadAction={() => actionCatalogoConfigGet("prestaciones_reclamadas")}
        addAction={(nombre) => actionCatalogoConfigCreate("prestaciones_reclamadas", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("prestaciones_reclamadas", id)}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<SafetyCertificateOutlined />}
        title="Tipos de pericial"
        desc="Tipos de prueba pericial disponibles en los expedientes de tribunal (ej. Contable, Médica, Valuación)"
        placeholder="Nombre del tipo de pericial..."
        emptyText="Sin tipos de pericial registrados"
        loadAction={() => actionCatalogoConfigGet("tipo_pericial")}
        addAction={(nombre) => actionCatalogoConfigCreate("tipo_pericial", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("tipo_pericial", id)}
      />

      <div className="cfg-divider" style={{ marginBottom: 50 }} />

      <CatalogoChips
        icon={<ApartmentOutlined />}
        title="Tipos de inspección ocular"
        desc="Tipos de prueba de inspección ocular disponibles en los expedientes de tribunal (ej. Nóminas, Tarjetas)"
        placeholder="Nombre del tipo de inspección ocular..."
        emptyText="Sin tipos de inspección ocular registrados"
        loadAction={() => actionCatalogoConfigGet("tipo_inspeccion_ocular")}
        addAction={(nombre) => actionCatalogoConfigCreate("tipo_inspeccion_ocular", nombre)}
        deleteAction={(id) => actionCatalogoConfigDelete("tipo_inspeccion_ocular", id)}
      />
    </div>
  );
}

// ── Sección Correos (tabla correos_users: correo / correo_gmail) ───────────
function CorreosSection() {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newCorreo, setNewCorreo] = useState("");
  const [newCorreoGmail, setNewCorreoGmail] = useState("");
  const [adding, setAdding] = useState(false);

  // Edición: se hace en un modal (correo, correo Gmail y autoridades juntos),
  // en vez de inputs sueltos dentro de la fila.
  const [editRow, setEditRow] = useState(null);
  const [editCorreo, setEditCorreo] = useState("");
  const [editCorreoGmail, setEditCorreoGmail] = useState("");
  const [editAutoridadIds, setEditAutoridadIds] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const autoridadesSlice = useSelector((state) => state.autoridades?.data || {});
  const autoridadesItems = Array.isArray(autoridadesSlice?.items) ? autoridadesSlice.items : [];
  const autoridadOptions = autoridadesItems
    .map((a) => ({ label: a?.nombre || `Autoridad ${a?.id}`, value: a?.id }))
    .filter((o) => o.label && o.value != null);

  useEffect(() => {
    dispatch(actionAutoridadesAllGet());
  }, [dispatch]);

  const loadItems = useCallback(async () => {
    try {
      const data = await dispatch(actionCorreosUsersGet());
      setItems(data?.items || []);
    } catch {
      notify.error("No se pudieron cargar los correos.");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const canAdd = newCorreo.trim() || newCorreoGmail.trim();

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    try {
      await dispatch(
        actionCorreosUsersCreate({
          correo: newCorreo.trim(),
          correo_gmail: newCorreoGmail.trim(),
        })
      );
      setNewCorreo("");
      setNewCorreoGmail("");
      notify.success("Correo agregado.");
      await loadItems();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo agregar el correo.");
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditCorreo(row.correo || "");
    setEditCorreoGmail(row.correo_gmail || "");
    setEditAutoridadIds((row.autoridades || []).map((a) => a.id));
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditRow(null);
  };

  const canSaveEdit = editCorreo.trim() || editCorreoGmail.trim();

  const saveEdit = async () => {
    if (!editRow) return;
    if (!canSaveEdit) {
      notify.warning("Debes llenar al menos correo o correo Gmail.");
      return;
    }
    setSavingEdit(true);
    try {
      await dispatch(
        actionCorreosUsersUpdate(editRow.id, {
          correo: editCorreo.trim(),
          correo_gmail: editCorreoGmail.trim(),
        })
      );
      await dispatch(actionCorreosUsersSetAutoridades(editRow.id, editAutoridadIds));
      notify.success("Invitado actualizado.");
      setEditRow(null);
      await loadItems();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "No se pudo actualizar el invitado.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: "¿Eliminar correo?",
      content: <span>Se eliminará el registro. Esta acción no se puede deshacer.</span>,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await dispatch(actionCorreosUsersDelete(row.id));
          notify.success("Correo eliminado.");
          await loadItems();
        } catch {
          notify.error("No se pudo eliminar el correo.");
        }
      },
    });
  };

  const columns = [
    {
      title: "Correo (Por Invitación)",
      dataIndex: "correo",
      render: (value) => value || <span className="cfg-tipo-empty-cell">—</span>,
    },
    {
      title: "Correo Gmail (Cuenta de Google)",
      dataIndex: "correo_gmail",
      render: (value) => value || <span className="cfg-tipo-empty-cell">—</span>,
    },
    {
      title: "Autoridades",
      key: "autoridades",
      width: 300,
      render: (_, row) => {
        const list = row.autoridades || [];
        if (list.length === 0) {
          return <span className="cfg-autoridades-view-empty">Todas las autoridades</span>;
        }
        return (
          <div className="cfg-autoridades-view-tags">
            {list.map((a) => (
              <Tag key={a.id} className="cfg-autoridades-view-tag">
                {a.nombre}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 150,
      render: (_, row) => (
        <div className="cfg-correo-actions">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
            className="cfg-correo-btn-edit"
          >
            Editar
          </Button>
          <Button
            size="small"
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(row)}
            className="cfg-correo-btn-delete"
            title="Eliminar"
          />
        </div>
      ),
    },
  ];

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <MailOutlined className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Invitados de la agenda</div>
          <div className="cfg-block-desc">
            Personas que se agregan automáticamente a los eventos de la agenda (invitación al evento y/o acceso al calendario).
            Llena al menos uno de los dos campos por fila. Elige las autoridades para las que aplica cada invitado; sin selección aplica a todas.
          </div>
        </div>
      </div>

      <div className="cfg-ciudad-form">
        <Input
          value={newCorreo}
          onChange={(e) => setNewCorreo(e.target.value)}
          placeholder="correo@ejemplo.com"
          className="cfg-tipo-input"
          onPressEnter={() => canAdd && handleAdd()}
          maxLength={150}
        />
        <Input
          value={newCorreoGmail}
          onChange={(e) => setNewCorreoGmail(e.target.value)}
          placeholder="correo@gmail.com"
          className="cfg-tipo-input"
          onPressEnter={() => canAdd && handleAdd()}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!canAdd}
          loading={adding}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>

      <Table
        className="cfg-correos-table"
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: "Sin correos registrados" }}
        size="middle"
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Editar invitado"
        open={!!editRow}
        onOk={saveEdit}
        onCancel={closeEdit}
        confirmLoading={savingEdit}
        okText="Guardar"
        cancelText="Cancelar"
        destroyOnClose
        maskClosable={!savingEdit}
        closable={!savingEdit}
      >
        <div className="cfg-edit-modal-body">
          <div className="cfg-edit-modal-field">
            <label className="cfg-edit-modal-label">Correo (Por Invitación)</label>
            <Input
              value={editCorreo}
              onChange={(e) => setEditCorreo(e.target.value)}
              placeholder="correo@ejemplo.com"
              maxLength={150}
            />
          </div>

          <div className="cfg-edit-modal-field">
            <label className="cfg-edit-modal-label">Correo Gmail (Cuenta de Google)</label>
            <Input
              value={editCorreoGmail}
              onChange={(e) => setEditCorreoGmail(e.target.value)}
              placeholder="correo@gmail.com"
            />
          </div>

          <div className="cfg-edit-modal-field">
            <label className="cfg-edit-modal-label">Autoridades</label>
            <Select
              mode="multiple"
              allowClear
              value={editAutoridadIds}
              onChange={setEditAutoridadIds}
              options={autoridadOptions}
              placeholder="Todas las autoridades"
              style={{ width: "100%" }}
              popupClassName="cfg-correo-autoridades-dropdown"
              listHeight={280}
            />
          </div>

          <div className="cfg-edit-modal-hint">
            Llena al menos correo o correo Gmail. Sin autoridades seleccionadas, el invitado aplica a todas.
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Módulos ────────────────────────────────────────────────────────────────
const MODULES = [
  {
    key: "general",
    label: "General",
    icon: <SettingOutlined />,
    color: "#374151",
    bg: "linear-gradient(140deg, #374151 0%, #9ca3af 100%)",
  },
  {
    key: "centro_conciliacion",
    label: "Centro Conciliación",
    icon: <ReconciliationOutlined />,
    color: "#0d7469",
    bg: "linear-gradient(140deg, #0d7469 0%, #2dd4bf 100%)",
  },
  {
    key: "tribunal",
    label: "Tribunal",
    icon: <AuditOutlined />,
    color: "#be123c",
    bg: "linear-gradient(140deg, #be123c 0%, #fb7185 100%)",
  },
  {
    key: "competencias",
    label: "Competencias",
    icon: <ApartmentOutlined />,
    color: "#7c3aed",
    bg: "linear-gradient(140deg, #7c3aed 0%, #a78bfa 100%)",
  },
  {
    key: "agenda",
    label: "Agenda",
    icon: <ScheduleOutlined />,
    color: "#0369a1",
    bg: "linear-gradient(140deg, #0369a1 0%, #38bdf8 100%)",
  },
];

const PANELS = {
  general: PanelGeneral,
  agenda: PanelAgenda,
  competencias: PanelCompetencias,
  centro_conciliacion: PanelCentroConciliacion,
  tribunal: PanelTribunal,
};

// ── Página ─────────────────────────────────────────────────────────────────
export default function ConfiguracionPage() {
  const [active, setActive] = useState("general");
  const activeModule = MODULES.find((m) => m.key === active);

  return (
    <main className="cfg-main">
      <div className="cfg-layout">

        <aside className="cfg-sidebar">
          <div className="cfg-sidebar-header">
            <Title level={5} className="cfg-sidebar-title">Configuración</Title>
          </div>

          <nav className="cfg-nav">
            {MODULES.map((mod) => (
              <button
                key={mod.key}
                type="button"
                className={`cfg-nav-item${active === mod.key ? " cfg-nav-item--active" : ""}`}
                onClick={() => setActive(mod.key)}
                style={active === mod.key ? { "--mod-color": mod.color } : {}}
              >
                <span
                  className="cfg-nav-icon"
                  style={{ background: active === mod.key ? mod.bg : undefined }}
                >
                  {mod.icon}
                </span>
                <span className="cfg-nav-label">{mod.label}</span>
                {active === mod.key && <span className="cfg-nav-active-bar" />}
              </button>
            ))}
          </nav>
        </aside>

        <section className="cfg-content">
          <div className="cfg-content-card">
            <div className="cfg-content-header">
              <span
                className="cfg-content-icon"
                style={{ background: activeModule?.bg }}
              >
                {activeModule?.icon}
              </span>
              <div>
                <Title level={3} className="cfg-content-title">{activeModule?.label}</Title>
                <Text type="secondary" className="cfg-content-subtitle">
                  Configuración del módulo
                </Text>
              </div>
            </div>
            <div className="cfg-content-inner-divider" />
            {React.createElement(PANELS[active] || PanelGeneral)}
          </div>
        </section>

      </div>
    </main>
  );
}
